import { BC_CONFIG } from "../authConfig";

const getBaseUrl = (companyId) => {
  const { baseUrl, tenantId, environment } = BC_CONFIG;
  return `${baseUrl}/${tenantId}/${environment}/api/v2.0/companies(${companyId})`;
};

// BC returns errors as `{"error":{"code":"...","message":"..."}}`. Extract just
// the message so the UI doesn't have to display raw JSON. Strips the trailing
// `CorrelationId: ...` that BC appends, since it's noise for end users.
function parseBcErrorMessage(text) {
  if (!text) return "";
  try {
    const parsed = JSON.parse(text);
    const msg = parsed?.error?.message || text;
    return msg.replace(/\s*CorrelationId:\s*[\w-]+\.?$/i, "").trim();
  } catch {
    return text;
  }
}

// Fetch company ID by name
export async function getCompanyId(token) {
  const { baseUrl, tenantId, environment, company } = BC_CONFIG;
  const url = `${baseUrl}/${tenantId}/${environment}/api/v2.0/companies`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  if (!res.ok) {
    const errText = await res.text();
    throw new Error(`Error obteniendo empresas: ${res.status} - ${parseBcErrorMessage(errText)}`);
  }
  const data = await res.json();
  const found = data.value?.find(
    (c) => c.name === company || c.displayName === company
  );
  if (!found) {
    // Return first company as fallback
    return data.value?.[0]?.id;
  }
  return found.id;
}

// Status values that BC v2.0 reports for documents waiting for approval.
// Recent BC versions return "In_x0020_Review" ("In Review"); older versions
// returned "Pending Approval" / "PendingApproval". The Spanish UI labels both
// as "Pendiente de aprobación", so we accept all variants.
const PENDING_APPROVAL_STATUSES = new Set([
  "in_x0020_review",
  "in review",
  "pending approval",
  "pendingapproval",
]);

// Header fields that don't trigger the BC currency-factor validation. Totals
// (totalAmount*, totalTaxAmount, discountAmount) are deliberately excluded —
// they cause Application_FieldValidationException when the LCY currency code
// is also registered in the Currencies table without exchange-rate entries
// (a known data-config quirk in this tenant). We compute totals client-side
// from the expanded purchaseOrderLines instead.
const HEADER_FIELDS = [
  "id",
  "number",
  "orderDate",
  "postingDate",
  "vendorId",
  "vendorNumber",
  "vendorName",
  "currencyId",
  "currencyCode",
  "requestedReceiptDate",
  "status",
  "fullyReceived",
  "lastModifiedDateTime",
].join(",");

// Line fields we pull at list-time. `locationId` is included so the home
// screen can show a per-order warehouse summary (single / Múltiples / Sin
// almacén) without having to fetch each order's lines individually.
const LINE_LIST_FIELDS = [
  "amountExcludingTax",
  "amountIncludingTax",
  "locationId",
].join(",");

function withComputedTotals(order) {
  const lines = order.purchaseOrderLines || [];
  const sum = (key) => lines.reduce((acc, l) => acc + (Number(l[key]) || 0), 0);
  const ex = sum("amountExcludingTax");
  const inc = sum("amountIncludingTax");
  return {
    ...order,
    totalAmountExcludingTax: ex,
    totalAmountIncludingTax: inc,
    totalTaxAmount: inc - ex,
  };
}

// Fetch all purchase orders. Uses $select + $expand so BC never has to compute
// header totals (which would require a currency factor). Totals are summed
// from the expanded lines client-side.
export async function getAllPurchaseOrders(token, companyId, top = 200) {
  const base = getBaseUrl(companyId);
  const query = [
    `$select=${HEADER_FIELDS}`,
    `$expand=purchaseOrderLines($select=${LINE_LIST_FIELDS})`,
    `$orderby=${encodeURIComponent("orderDate desc")}`,
    `$top=${top}`,
  ].join("&");
  const url = `${base}/purchaseOrders?${query}`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  if (!res.ok) {
    const errText = await res.text();
    throw new Error(`Error obteniendo pedidos: ${res.status} - ${parseBcErrorMessage(errText)}`);
  }
  const data = await res.json();
  return (data.value || []).map(withComputedTotals);
}

// Fetch purchase orders pending approval. Filters client-side because BC's
// `status` enum spelling has varied between platform updates and the volumes
// here are small enough that pulling and filtering in memory is fine.
export async function getPurchaseOrdersPendingApproval(token, companyId) {
  const all = await getAllPurchaseOrders(token, companyId, 500);
  return all.filter((o) => {
    const s = (o.status || "").toString().trim().toLowerCase();
    return PENDING_APPROVAL_STATUSES.has(s);
  });
}

// Fetch all locations (warehouses) so we can resolve `locationId` GUIDs to
// human-readable codes/names in the line detail view.
export async function getLocations(token, companyId) {
  const base = getBaseUrl(companyId);
  const url = `${base}/locations`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  if (!res.ok) {
    const errText = await res.text();
    throw new Error(`Error obteniendo almacenes: ${res.status} - ${parseBcErrorMessage(errText)}`);
  }
  const data = await res.json();
  return data.value || [];
}

// Fetch purchase order lines
export async function getPurchaseOrderLines(token, companyId, orderId) {
  const base = getBaseUrl(companyId);
  const url = `${base}/purchaseOrders(${orderId})/purchaseOrderLines`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  if (!res.ok) {
    const errText = await res.text();
    throw new Error(`Error obteniendo líneas: ${res.status} - ${parseBcErrorMessage(errText)}`);
  }
  const data = await res.json();
  return data.value || [];
}

// Approval actions go through our custom AL extension API (page 70000) deployed
// on the BC tenant. The extension exposes `approvalEntries` with `approve` and
// `reject` actions bound to each entry.
const CUSTOM_API_PATH = "api/adelante/pwa/v1.0";
// Purchase Header table ID — used to filter approval entries to only those
// belonging to purchase orders.
const PURCHASE_HEADER_TABLE_ID = 38;

const getCustomApiBase = (companyId) => {
  const { baseUrl, tenantId, environment } = BC_CONFIG;
  return `${baseUrl}/${tenantId}/${environment}/${CUSTOM_API_PATH}/companies(${companyId})`;
};

async function getOpenApprovalForOrder(token, companyId, orderNumber) {
  const base = getCustomApiBase(companyId);
  // The AL page returns the full approval history (not just the user's open
  // ones), and $filter has been unreliable on it ("Error in query syntax"
  // even for plain Code fields like documentNo). So we fetch a large window
  // and match client-side. 5000 is BC's standard hard cap.
  const url = `${base}/approvalEntries?$top=5000`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  if (!res.ok) {
    const errText = await res.text();
    throw new Error(parseBcErrorMessage(errText) || `Error consultando aprobaciones (${res.status})`);
  }
  const data = await res.json();
  const approvals = data.value || [];
  const sameDoc = approvals.filter((a) => a.documentNo === orderNumber);
  const match = sameDoc.find(
    (a) =>
      Number(a.tableId) === PURCHASE_HEADER_TABLE_ID &&
      (a.status || "").toString().toLowerCase() === "open"
  );
  if (!match) {
    if (sameDoc.length === 0) {
      throw new Error(
        `No hay solicitudes de aprobación para ${orderNumber} a tu nombre. Verifica que el flujo se haya enviado y que tú seas el aprobador.`
      );
    }
    const states = sameDoc.map((a) => a.status).join(", ");
    throw new Error(
      `La solicitud de aprobación para ${orderNumber} ya no está abierta (estado: ${states}).`
    );
  }
  return match;
}

async function postApprovalAction(token, companyId, approvalId, action) {
  const base = getCustomApiBase(companyId);
  // Bound actions on BC AL pages — including custom API pages with a
  // custom APIPublisher/APIGroup — are exposed under `Microsoft.NAV.<action>`
  // in OData, not the publisher's namespace.
  const url = `${base}/approvalEntries(${approvalId})/Microsoft.NAV.${action}`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: "{}",
  });
  if (!res.ok) {
    const errText = await res.text();
    throw new Error(parseBcErrorMessage(errText) || `BC devolvió ${res.status}`);
  }
  return true;
}

// The AL custom API page exposes the row key as `systemId` (not `id`). Use
// `id` as a fallback in case the page is reconfigured later.
const approvalKey = (approval) => approval.systemId || approval.id;

export async function approvePurchaseOrder(token, companyId, orderNumber) {
  const approval = await getOpenApprovalForOrder(token, companyId, orderNumber);
  return postApprovalAction(token, companyId, approvalKey(approval), "approve");
}

export async function rejectPurchaseOrder(token, companyId, orderNumber) {
  const approval = await getOpenApprovalForOrder(token, companyId, orderNumber);
  return postApprovalAction(token, companyId, approvalKey(approval), "reject");
}
